import { useState, useRef, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import styles from './CreateHomeSlider.module.scss';
import CreateNewsHeader from '../../../Molecules/NewsAndEvents/CreateNewsHeader';
import folderIcon from '../../../../assets/Imagess/folderICon.svg';
import CustomSelect from '../../../Atoms/forms/customSelect/CustomSelect';
import uploadIcon from '../../../../assets/Imagess/upload-icon.svg';
import hashIcon from '../../../../assets/icons/formActions/inputs/hash.svg';
import { useMutation } from 'react-query';
import axios from 'axios';
import {
  API_URL,
  quillFormats,
  quillModules,
  securityHeaders,
} from '../../../../Constants';
import { useNavigate, useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import noImageFound from '../../../../assets/Imagess/no-image-found.jpg';
import './Quill.css';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import TextInput from '../../../Atoms/Inputs/TextInput';
import { useToast } from '../../../Molecules/Custom/CustomToaster/Toast';
import Loader from '../../../Molecules/Loader/Loader';
import he from 'he';
import { sanitizeAndValidateUrl } from '../../../../app/helpers/sanitizeAndValidateUrl';
import TextEditor from '../../../Molecules/Email/TextEditor';
import {
  useFetchHomePage,
  useFetchSliderById,
} from '../../../../Hooks/HomeSlider';
import quoteIcon from '../../../../assets/Imagess/quote.svg';
import linkIcon from '../../../../assets/Imagess/linkIcon.svg';
import PreviewSlide from '../../../Molecules/HomeSlider/PreviewSlide';

const MAX_BYTES = 50 * 1024 * 1024;
const looksLikeVideo = (v) =>
  /\.(mp4|webm|ogg|mov|m4v|3gp|avi|mkv)(\?.*)?$/i.test(v || '');

// -----------------------------------------------------------------------------
// Slide hyperlink. The website reads these three fields together: `BackGroundType`
// selects WHICH of the other two is meaningful, so exactly one of them is ever
// sent, and the unused one is cleared rather than left stale.
//
//   0 - no link. The slide is not clickable. `BackGroundFile` and
//       `BackGroundLink` are both ignored.
//   1 - external URL, taken from `BackGroundLink`.
//   2 - uploaded file, taken from `BackGroundFile`; the website resolves it
//       against the record's own `blobUrl`, exactly as it does for `Media1`.
//
// The website opens the result in a new tab. It already tolerates a URL typed
// without a scheme, but one is added here on submit so the stored value is
// unambiguous for anyone reading the API directly.
// -----------------------------------------------------------------------------
const BG_TYPE_NONE = 0;
const BG_TYPE_LINK = 1;
const BG_TYPE_FILE = 2;

const BG_TYPE_OPTIONS = [
  { value: BG_TYPE_NONE, text: 'بدون رابط' },
  { value: BG_TYPE_LINK, text: 'رابط خارجي' },
  { value: BG_TYPE_FILE, text: 'ملف مرفق' },
];

// The attachment is previewed in-page, so it is restricted to the two kinds the
// browser can actually render inline: a PDF and an image. Office formats were
// accepted before but could only ever be downloaded, never previewed.
const BG_FILE_ACCEPT = 'application/pdf,image/*';
// The attachment has its own ceiling, separate from `MAX_BYTES`, which stays at
// 50MB for the slide media above.
const BG_MAX_BYTES = 100 * 1024 * 1024;
const BG_FILE_RULES = 'PDF أو صورة فقط · أقصى حجم 100MB';
const BG_FILE_SIZE_ERROR = 'حجم الملف أكبر من 100 ميغابايت (الحد الأقصى 100MB)';
const BG_FILE_TYPE_ERROR = 'نوع الملف غير مدعوم. يُسمح بملفات PDF والصور فقط';

const isPdfType = (file) => file?.type === 'application/pdf';
const isImageType = (file) => !!file?.type?.startsWith('image/');
const isAllowedBgFile = (file) => isPdfType(file) || isImageType(file);

// Same question for a file already stored on the record, where only the name is
// known - the API does not return a MIME type for it.
const looksLikePdf = (v) => /\.pdf(\?.*)?$/i.test(v || '');
const looksLikeImage = (v) =>
  /\.(png|jpe?g|gif|webp|svg|bmp|avif)(\?.*)?$/i.test(v || '');

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

// Host with at least one dot, optional scheme, optional path/query. Deliberately
// permissive - it rejects typos like "dubaipolice" without demanding a scheme.
const URL_PATTERN =
  /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(:\d{1,5})?(\/[^\s]*)?$/i;

const withScheme = (value) => {
  const v = String(value || '').trim();
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

const CreateHomeSlider = () => {
  const fileInputRef = useRef(null);
  const params = useParams();
  const customizedToast = useToast();
  const navigate = useNavigate();
  const { id } = params;
  const [imageSrc, setImageSrc] = useState('');
  const [isVideo1, setIsVideo1] = useState(false);
  const { data: slider, isLoading, refetch } = useFetchSliderById(id);
  const formikRef = useRef(null);
  const { data: homeSliders } = useFetchHomePage();
  const [previewOpen, setPreviewOpen] = useState(false);
  const bgFileInputRef = useRef(null);
  // The file already stored on the record, as a name plus a resolvable URL.
  // Kept separate from the Formik value, which only ever holds a NEWLY picked
  // File - the same split `Media1` uses, so an edit that does not touch the
  // attachment does not re-upload it.
  const [bgFileName, setBgFileName] = useState('');
  const [bgFileUrl, setBgFileUrl] = useState('');
  // Object URL for a NEWLY picked attachment, so it can be previewed before it
  // is uploaded. Held in state (not derived at render) because every
  // `createObjectURL` call leaks until it is revoked.
  const [bgFilePreview, setBgFilePreview] = useState(null);
  // Mirrors `bgFilePreview` so the unmount cleanup can revoke the live URL
  // without going through a state updater, which React skips once the
  // component is gone - that would leak the object URL.
  const bgFilePreviewRef = useRef(null);

  // Swap in a new preview and revoke the one it replaces. `null` just clears.
  const setBgPreviewFromFile = (file) => {
    if (bgFilePreviewRef.current?.url) {
      URL.revokeObjectURL(bgFilePreviewRef.current.url);
    }
    const next = file
      ? {
          url: URL.createObjectURL(file),
          isPdf: isPdfType(file),
          isImage: isImageType(file),
        }
      : null;
    bgFilePreviewRef.current = next;
    setBgFilePreview(next);
  };

  // Release the last object URL when the page unmounts.
  useEffect(() => {
    return () => {
      if (bgFilePreviewRef.current?.url) {
        URL.revokeObjectURL(bgFilePreviewRef.current.url);
        bgFilePreviewRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(id) && id !== undefined) {
      refetch();
    }
  }, [id]);

  useEffect(() => {
    if (!isEmpty(slider)) {
      const sliderData = slider.data || {};
      formikRef.current.setValues({
        Id: sliderData.id || '',
        SlideType: sliderData?.slideType,
        SequenceId:
          sliderData?.sequenceId >= 1 && sliderData?.sequenceId <= 10
            ? sliderData?.sequenceId
            : null,
        Title: sliderData.title || '',
        Description: he.decode(sliderData.description || ''),
        Media1: null,
        WithSound: !!sliderData?.withSound,
        WithHue: !!sliderData?.withHue,
        BackGroundType: Number(sliderData?.backGroundType ?? BG_TYPE_NONE),
        BackGroundLink: sliderData?.backGroundLink || '',
        BackGroundFile: null,
      });

      setBgFileName(sliderData?.backGroundFile || '');
      setBgFileUrl(
        sliderData?.backGroundFile
          ? sanitizeAndValidateUrl(
              `${sliderData?.blobUrl || ''}${sliderData.backGroundFile}`
            )
          : ''
      );

      const fullUrl = sanitizeAndValidateUrl(
        `${slider?.data?.blobUrl || ''}${slider?.data?.media1 || ''}`
      );
      setImageSrc(fullUrl);
      setIsVideo1(looksLikeVideo(fullUrl));
    }
  }, [slider]);

  const validationSchema = Yup.object().shape({
    Id: Yup.string().nullable().uuid('معرّف غير صالح'),
    SlideType: Yup.number().oneOf([0, 1]).required('مطلوب تعبئته'),
    SequenceId: Yup.number()
      .typeError('أدخل رقمًا صالحًا')
      .integer('يجب أن يكون عددًا صحيحًا')
      .required('مطلوب تعبئته'),
    Title: Yup.string().when('SlideType', {
      is: 1,
      then: (s) =>
        s.max(103, 'يجب ألا يتجاوز 103 حرفًا').required('مطلوب تعبئته'),
      otherwise: (s) => s.required('مطلوب تعبئته'),
    }),
    Description: Yup.string().when('SlideType', {
      is: 0,
      then: (s) => s.required('مطلوب تعبئته'),
      otherwise: (s) => s.nullable().notRequired(),
    }),
    Media1: Yup.mixed()
      .nullable()
      .test(
        'fileSize',
        'حجم الملف أكبر من 50 ميغابايت (الحد الأقصى 50MB)',
        (value) => {
          if (!value) return true;
          return value.size <= MAX_BYTES;
        }
      ),
    WithSound: Yup.boolean().nullable(),
    WithHue: Yup.boolean().nullable(),
    BackGroundType: Yup.number()
      .oneOf([BG_TYPE_NONE, BG_TYPE_LINK, BG_TYPE_FILE])
      .required('مطلوب تعبئته'),
    BackGroundLink: Yup.string().when('BackGroundType', {
      is: BG_TYPE_LINK,
      then: (s) =>
        s
          .required('مطلوب تعبئته')
          .matches(URL_PATTERN, 'أدخل رابطًا صالحًا، مثال: www.example.com'),
      otherwise: (s) => s.nullable().notRequired(),
    }),
    BackGroundFile: Yup.mixed()
      .nullable()
      .test(
        'fileSize',
        BG_FILE_SIZE_ERROR,
        (value) => !value || value.size <= BG_MAX_BYTES
      )
      .test('fileType', BG_FILE_TYPE_ERROR, (value) => {
        // `accept` only filters the picker dialog; it is not enforcement. A file
        // dragged in, or chosen with the filter switched off, still lands here.
        if (!value) return true;
        return isAllowedBgFile(value);
      })
      // Required only when no file is already stored on the record, so editing
      // a slide that already has one does not force a re-upload.
      .test('required', 'مطلوب تعبئته', function (value) {
        if (this.parent.BackGroundType !== BG_TYPE_FILE) return true;
        return !!value || !!bgFileName;
      }),
  });

  const createNews = useMutation({
    mutationFn: (fields) => {
      return axios.post(API_URL + `/api/HomePage/HomePages`, fields, {
        headers: securityHeaders,
      });
    },
    onSuccess: () => {
      customizedToast.open(
        id ? 'تم تحديث الشريط بنجاح' : 'تم إنشاء الشريط بنجاح'
      );
      setTimeout(() => {
        formikRef.current.resetForm();
        navigate(-1);
      }, 2000);
    },
    onError: (error) => {
      toast.error(error?.message);
    },
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    const sliderId = id
      ? id
      : values.Id || '00000000-0000-0000-0000-000000000000';
    formData.append('Id', sliderId);
    formData.append('SlideType', String(values.SlideType ?? ''));
    formData.append('SequenceId', String(values.SequenceId ?? ''));
    formData.append('Title', values.Title ?? '');
    formData.append('Description', values.Description ?? '');
    if (values.Media1) formData.append('Media1', values.Media1);
    formData.append('WithSound', String(!!values.WithSound));
    formData.append('WithHue', String(!!values.WithHue));

    // Exactly one of the two payload fields is sent, decided by the type. The
    // other is sent EMPTY rather than omitted, so switching a slide from a link
    // back to none actually clears the old value server-side instead of leaving
    // a stale URL the website would still make clickable.
    const bgType = Number(values.BackGroundType ?? BG_TYPE_NONE);
    formData.append('BackGroundType', String(bgType));
    formData.append(
      'BackGroundLink',
      bgType === BG_TYPE_LINK ? withScheme(values.BackGroundLink) : ''
    );
    if (bgType === BG_TYPE_FILE && values.BackGroundFile) {
      formData.append('BackGroundFile', values.BackGroundFile);
    }

    createNews.mutate(formData);
  };

  const triggerFormSubmission = () => {
    formikRef.current.submitForm();
  };

  if (isLoading) {
    return <Loader />;
  }

  const canPreview = !!(formikRef.current?.values?.Media1 || imageSrc);

  return (
    <div className={styles['action-programs']} style={{ position: 'relative' }}>
      <div className={styles['container']}>
        <CreateNewsHeader
          handleSubmit={triggerFormSubmission}
          title={!isEmpty(id) ? 'تحديث الشريحة' : 'إنشاء شريحة'}
          onPreviewClick={() => canPreview && setPreviewOpen(true)}
        />
      </div>
      <div className={styles['container']} style={{ paddingTop: '0px' }}>
        <Formik
          innerRef={formikRef}
          initialValues={{
            Id: '',
            SlideType: 0,
            SequenceId: null,
            Title: '',
            Description: '',
            Media1: null,
            WithSound: false,
            WithHue: false,
            BackGroundType: BG_TYPE_NONE,
            BackGroundLink: '',
            BackGroundFile: null,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            setFieldValue,
            setFieldError,
            errors,
            touched,
            setFieldTouched,
          }) => {
            const usedSeqSet = new Set(
              (homeSliders?.data || [])
                .filter(
                  (s) =>
                    s?.sequenceId >= 1 && s?.sequenceId <= 10 && s?.id !== id
                )
                .map((s) => Number(s.sequenceId))
            );
            const seqOptions = Array.from({ length: 10 }, (_, i) => i + 1)
              .filter((n) => !usedSeqSet.has(n))
              .map((n) => ({ value: n, text: String(n) }));

            return (
              <Form>
                <div className={styles['step-container']}>
                  <div className={`${styles['rtl']}`}>
                    <CustomSelect
                      label='نوع العنوان'
                      placeholder='اختر نوع العنوان'
                      icon={folderIcon}
                      options={[
                        { value: 0, text: 'عنوان / وصف' },
                        { value: 1, text: 'شعار' },
                      ]}
                      name='SlideType'
                      value={values.SlideType}
                      onChange={(name, value) => setFieldValue(name, value)}
                      errors={errors.SlideType}
                      touched={touched.SlideType}
                      right
                    />
                  </div>
                  <CustomSelect
                    label='التسلسل'
                    placeholder='اختر التسلسل'
                    icon={hashIcon}
                    options={seqOptions}
                    name='SequenceId'
                    value={values.SequenceId}
                    onChange={(name, value) => setFieldValue(name, value)}
                    errors={errors.SequenceId}
                    touched={touched.SequenceId}
                    right
                  />
                </div>

                <div className={styles['desc-container']}>
                  {0 === values?.SlideType ? (
                    <>
                      <TextInput
                        label='العنوان'
                        placeholder='أدخل العنوان'
                        icon={folderIcon}
                        style={{
                          width: '100%',
                          justifyContent: 'flex-start',
                          marginBottom: '26px',
                        }}
                        containerStyle={{ padding: '0px' }}
                        name='Title'
                        value={values.Title}
                        errors={errors.Title}
                        touched={touched.Title}
                        onChange={(name, value) => setFieldValue(name, value)}
                        left
                      />
                      <div className={`${styles['quill-container']}`}>
                        <TextEditor
                          label='الوصف'
                          value={values.Description}
                          onChange={(name, value) => {
                            setFieldValue(name, value);
                          }}
                          modules={quillModules}
                          formats={quillFormats}
                          name='Description'
                          errors={errors.Description && touched.Description}
                          right
                        />
                      </div>
                    </>
                  ) : (
                    (() => {
                      return (
                        <div className={styles['desc-quote']}>
                          <img src={quoteIcon} className={styles['q-open']} />
                          <img src={quoteIcon} className={styles['q-close']} />
                          <span
                            className={`${styles.line} ${styles['line-top']}`}
                          />
                          <span
                            className={`${styles.line} ${styles['line-right']}`}
                          />
                          <span
                            className={`${styles.line} ${styles['line-left']}`}
                          />
                          <span
                            className={`${styles.line} ${styles['line-bottom']}`}
                          />

                          <div className={styles.head}>
                            <span className={styles.label}>تفاصيل الشعار</span>
                            <span className={styles.count}>
                              max character: 103
                            </span>
                          </div>

                          <textarea
                            className={styles['input']}
                            name='Description'
                            dir='rtl'
                            maxLength={103}
                            placeholder='أدخل الوصف'
                            value={values.Title || ''}
                            onChange={(e) =>
                              setFieldValue('Title', e.target.value)
                            }
                          />
                          {errors.Title && touched.Title && (
                            <div className={styles['error-message']}>
                              <p>{errors.Title}</p>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  )}
                </div>

                <div className={styles['desc-container']}>
                  <label className={styles['quill-label']}>
                    رفع الخلفية (صورة أو فيديو){' '}
                  </label>
                  {errors.Media1 && (
                    <div className={styles['error-message']}>
                      <p>{errors.Media1}</p>
                    </div>
                  )}
                  <div
                    className={styles['upload-container']}
                    onClick={() => {
                      if (fileInputRef.current) fileInputRef.current.value = '';
                      fileInputRef.current.click();
                    }}
                  >
                    <input
                      type='file'
                      accept='image/*,video/*'
                      className='form-control form-body-input'
                      style={{ display: 'none' }}
                      ref={fileInputRef}
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0];

                        if (file && file.size > 50 * 1024 * 1024) {
                          setFieldTouched('Media1', true, false);
                          setFieldError(
                            'Media1',
                            'حجم الملف أكبر من 50 ميغابايت (الحد الأقصى 50MB)'
                          );
                          if (fileInputRef.current)
                            fileInputRef.current.value = '';
                          setImageSrc('');
                          setIsVideo1(false);
                          return;
                        }

                        setFieldError('Media1', undefined);
                        setFieldTouched('Media1', true, false);
                        setFieldValue('Media1', file || null, false);
                        setIsVideo1(!!file && file.type?.startsWith('video'));
                        setImageSrc(file ? URL.createObjectURL(file) : '');
                      }}
                    />
                    {values.Media1 || imageSrc ? (
                      <div className={styles['image-container']}>
                        {(
                          values.Media1
                            ? values.Media1.type?.startsWith('video')
                            : isVideo1
                        ) ? (
                          <video
                            className={styles['uploaded-image']}
                            src={
                              values.Media1
                                ? URL.createObjectURL(values.Media1)
                                : imageSrc
                            }
                            controls
                            playsInline
                            preload='metadata'
                          />
                        ) : (
                          <img
                            className={styles['uploaded-image']}
                            src={
                              values.Media1
                                ? URL.createObjectURL(values.Media1)
                                : imageSrc
                            }
                            alt='selected-image'
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null;
                              currentTarget.src = noImageFound;
                            }}
                          />
                        )}

                        <button
                          className='delete-button-images'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setFieldValue('Media1', null);
                            setImageSrc('');
                            setIsVideo1(false);
                            setFieldError('Media1', undefined);
                            if (fileInputRef.current)
                              fileInputRef.current.value = '';
                          }}
                          title='حذف الصورة'
                          type='button'
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className={styles['upload-icon-container']}>
                        <img
                          className={styles['upload-icon']}
                          src={uploadIcon}
                        />
                        <div className={styles['text-container']}>
                          <p className={styles['select-image']}>
                            رفع الخلفية (صورة أو فيديو){' '}
                          </p>
                          <p className={styles['size']}>أقصى حجم: 50MB</p>
                        </div>
                        <p className='select-sizes'>
                          حجم الصورة: العرض: 406px الارتفاع: 292px
                        </p>
                      </div>
                    )}
                  </div>
                  <div className={styles['settings-container']}>
                    <label className={styles['quill-label']}>
                      تحكم الصوت بالفيديو
                    </label>
                    <div className={styles['radio-buttons']}>
                      <div className={styles['radio-item']}>
                        <input
                          type='radio'
                          name='WithSound'
                          checked={!!values.WithSound}
                          onChange={() => setFieldValue('WithSound', true)}
                        />
                        <label>التشغيل بشكل تلقائي</label>
                      </div>
                      <div className={styles['radio-item']}>
                        <input
                          type='radio'
                          name='WithSound'
                          checked={!values.WithSound}
                          onChange={() => setFieldValue('WithSound', false)}
                        />{' '}
                        <label>الإيقاف بشكل تلقائي</label>
                      </div>
                    </div>
                  </div>
                  <div className={styles['settings-container']}>
                    <label className={styles['quill-label']}>
                      إضافة طبقة داكنة على الخلفية
                    </label>
                    <div className={styles['radio-buttons']}>
                      <div className={styles['radio-item']}>
                        <input
                          type='radio'
                          name='WithHue'
                          checked={!!values.WithHue}
                          readOnly
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setFieldValue('WithHue', !values.WithHue);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                              e.preventDefault();
                              setFieldValue('WithHue', !values.WithHue);
                            }
                          }}
                        />
                        <label>إضافة الطبقة</label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles['desc-container']}>
                  <label className={styles['quill-label']}>
                    الرابط عند الضغط على الشريحة
                  </label>

                  <div className={`${styles['rtl']}`}>
                    <CustomSelect
                      label='نوع الرابط'
                      placeholder='اختر نوع الرابط'
                      icon={folderIcon}
                      options={BG_TYPE_OPTIONS}
                      name='BackGroundType'
                      value={values.BackGroundType}
                      onChange={(name, value) => {
                        const next = Number(value);
                        setFieldValue(name, next);
                        // Drop whatever the other branch was holding, so a
                        // slide cannot be saved with a link AND a file.
                        if (next !== BG_TYPE_LINK) {
                          setFieldValue('BackGroundLink', '');
                        }
                        if (next !== BG_TYPE_FILE) {
                          setFieldValue('BackGroundFile', null);
                          setFieldError('BackGroundFile', undefined);
                          setBgPreviewFromFile(null);
                          if (bgFileInputRef.current) {
                            bgFileInputRef.current.value = '';
                          }
                        }
                      }}
                      errors={errors.BackGroundType}
                      touched={touched.BackGroundType}
                      right
                    />
                  </div>

                  {values.BackGroundType === BG_TYPE_LINK && (
                    <TextInput
                      label='الرابط'
                      placeholder='https://www.dubaipolice.ac.ae'
                      icon={linkIcon}
                      style={{
                        width: '100%',
                        justifyContent: 'flex-start',
                        marginBottom: '26px',
                      }}
                      containerStyle={{ padding: '0px' }}
                      name='BackGroundLink'
                      value={values.BackGroundLink}
                      errors={errors.BackGroundLink}
                      touched={touched.BackGroundLink}
                      onChange={(name, value) => setFieldValue(name, value)}
                      left
                    />
                  )}

                  {values.BackGroundType === BG_TYPE_FILE && (
                    <>
                      {errors.BackGroundFile && touched.BackGroundFile && (
                        <div className={styles['error-message']}>
                          <p>{errors.BackGroundFile}</p>
                        </div>
                      )}
                      <div
                        className={styles['upload-container']}
                        onClick={() => {
                          if (bgFileInputRef.current) {
                            bgFileInputRef.current.value = '';
                            bgFileInputRef.current.click();
                          }
                        }}
                      >
                        <input
                          type='file'
                          accept={BG_FILE_ACCEPT}
                          className='form-control form-body-input'
                          style={{ display: 'none' }}
                          ref={bgFileInputRef}
                          onChange={(event) => {
                            const file = event.currentTarget.files?.[0];
                            setFieldTouched('BackGroundFile', true, false);

                            const reject = (message) => {
                              setFieldError('BackGroundFile', message);
                              setFieldValue('BackGroundFile', null, false);
                              setBgPreviewFromFile(null);
                              if (bgFileInputRef.current) {
                                bgFileInputRef.current.value = '';
                              }
                            };

                            if (file && !isAllowedBgFile(file)) {
                              reject(BG_FILE_TYPE_ERROR);
                              return;
                            }

                            if (file && file.size > BG_MAX_BYTES) {
                              reject(
                                `حجم الملف ${formatFileSize(
                                  file.size
                                )} يتجاوز الحد الأقصى 100MB`
                              );
                              return;
                            }

                            setFieldError('BackGroundFile', undefined);
                            setFieldValue('BackGroundFile', file || null, false);
                            setBgPreviewFromFile(file || null);
                          }}
                        />

                        {values.BackGroundFile || bgFileName ? (
                          (() => {
                            // A newly picked file previews from its object URL;
                            // otherwise fall back to the file already on the
                            // record, whose kind is inferred from its name.
                            const isNew = !!values.BackGroundFile;
                            const previewUrl = isNew
                              ? bgFilePreview?.url
                              : bgFileUrl;
                            const displayName = isNew
                              ? values.BackGroundFile.name
                              : bgFileName;
                            const showPdf = isNew
                              ? bgFilePreview?.isPdf
                              : looksLikePdf(bgFileName);
                            const showImage = isNew
                              ? bgFilePreview?.isImage
                              : looksLikeImage(bgFileName);

                            return (
                              <div className={styles['image-container']}>
                                {previewUrl && (showPdf || showImage) && (
                                  // Clicks stay inside the preview so it can be
                                  // scrolled and read; the container's own
                                  // handler would otherwise reopen the picker.
                                  <div
                                    className={styles['bg-file-preview']}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {showPdf ? (
                                      <iframe
                                        src={previewUrl}
                                        title={displayName}
                                        className={
                                          styles['bg-file-preview-frame']
                                        }
                                      />
                                    ) : (
                                      <img
                                        src={previewUrl}
                                        alt={displayName}
                                        className={
                                          styles['bg-file-preview-image']
                                        }
                                      />
                                    )}
                                  </div>
                                )}

                                <div className={styles['text-container']}>
                                  <p className={styles['select-image']}>
                                    {displayName}
                                    {isNew &&
                                      ` (${formatFileSize(
                                        values.BackGroundFile.size
                                      )})`}
                                  </p>
                                  {!isNew && bgFileUrl && (
                                    <a
                                      href={bgFileUrl}
                                      target='_blank'
                                      rel='noreferrer'
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      فتح الملف الحالي
                                    </a>
                                  )}
                                  <p className={styles['size']}>
                                    {BG_FILE_RULES}
                                  </p>
                                </div>

                                <button
                                  className='delete-button-images'
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setFieldValue('BackGroundFile', null);
                                    setBgFileName('');
                                    setBgFileUrl('');
                                    setBgPreviewFromFile(null);
                                    setFieldError('BackGroundFile', undefined);
                                    if (bgFileInputRef.current) {
                                      bgFileInputRef.current.value = '';
                                    }
                                  }}
                                  title='حذف الملف'
                                  type='button'
                                >
                                  ✕
                                </button>
                              </div>
                            );
                          })()
                        ) : (
                          <div className={styles['upload-icon-container']}>
                            <img
                              className={styles['upload-icon']}
                              src={uploadIcon}
                              alt=''
                            />
                            <div className={styles['text-container']}>
                              <p className={styles['select-image']}>
                                رفع الملف المرفق
                              </p>
                              <p className={styles['size']}>{BG_FILE_RULES}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <button
                  type='submit'
                  className={styles['submit-button']}
                  style={{ display: 'none' }}
                >
                  إرسال
                </button>
              </Form>
            );
          }}
        </Formik>
      </div>
      <PreviewSlide
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        mediaUrl={
          formikRef.current?.values?.Media1
            ? URL.createObjectURL(formikRef.current?.values?.Media1)
            : imageSrc
        }
        isVideo={
          formikRef.current?.values?.Media1
            ? formikRef.current?.values.Media1.type?.startsWith('video')
            : looksLikeVideo(
                formikRef.current?.values?.Media1
                  ? formikRef.current?.values.Media1.name
                  : imageSrc
              )
        }
        title={formikRef.current?.values?.Title}
        slideType={formikRef.current?.values?.SlideType}
        description={
          formikRef.current?.values?.SlideType === 0
            ? formikRef.current?.values?.Description
            : ''
        }
        withSound={!!formikRef.current?.values?.WithSound}
        withHue={!!formikRef.current?.values?.WithHue}
      />
    </div>
  );
};

export default CreateHomeSlider;
